import type { SessionPayload } from "./AuthenticatedApp";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="func-panel" style={{ marginBottom: 16 }}>
      <div className="func-table-head"><h2>{title}</h2></div>
      <div className="autonomous-body" style={{ lineHeight: 1.65, fontSize: 13 }}>{children}</div>
    </section>
  );
}

function Steps({ items }: { items: string[] }) {
  return (
    <ol style={{ paddingLeft: 20, margin: "8px 0 0" }}>
      {items.map((item, i) => <li key={i} style={{ marginBottom: 6 }} dangerouslySetInnerHTML={{ __html: item }} />)}
    </ol>
  );
}

function Tip({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ background: "#f0faf9", border: "1px solid #b2ddd9", borderRadius: 6, padding: "10px 13px", margin: "10px 0", fontSize: 12 }}>
      💡 {children}
    </div>
  );
}

export function HelpWorkspace({ session }: { session: SessionPayload }) {
  const p = session.user.permissions;
  const owner = session.user.isTenantOwner;
  const all = owner || p.includes("*");
  const can = (key: string) => all || p.includes(key);
  const product = (name: "MAINTENANCE" | "DISPATCH" | "APPOINTMENTS") =>
    session.user.products.includes(name);

  return (
    <div className="help-workspace">
      <div className="func-heading">
        <div>
          <h1>Help</h1>
          <p>Guidance for every area of your TaskRoute workspace.</p>
        </div>
      </div>

      {/* ── Getting started ── */}
      <Section title="Getting started">
        <p>TaskRoute is organised into <b>products</b> — Maintenance, Dispatch, and Appointments. Your administrator assigns you a product seat and a group, which controls what you can see and do.</p>
        <p style={{ marginTop: 8 }}>Use the <b>product switcher</b> in the top bar to move between products. The sidebar shows only the sections relevant to your permissions.</p>
        <Tip>If a section you expect to see is missing, ask your administrator to check your group permissions.</Tip>
      </Section>

      {/* ── Maintenance ── */}
      {product("MAINTENANCE") && (
        <>
          {(can("maintenance.work_orders.claim") || can("maintenance.work_orders.complete")) && (
            <Section title="Work orders">
              <p>Work orders record all maintenance tasks. The lifecycle runs: <b>Open → Claimed → In Progress → Complete → Signed off</b>.</p>
              <Steps items={[
                "Open <b>Work orders</b> and press <b>New work order</b> to report a fault.",
                "An engineer claims the job or a manager assigns it.",
                "While working, add <b>labour time</b>, <b>notes</b>, and <b>photos or PDFs</b> via the attachments area.",
                "If the fault cannot be fully repaired, record a <b>temporary fix</b> and set a <b>permanent repair due date</b>.",
                "Mark the job <b>Complete</b> when work is done, then a signatory approves it.",
                "Downtime events can be recorded at any stage to track production impact.",
              ]} />
              <Tip>Work orders assigned to you appear highlighted at the top of the list.</Tip>
            </Section>
          )}

          {can("maintenance.ppm.manage") && (
            <Section title="Planned Preventive Maintenance (PPM)">
              <p>PPM schedules recurring maintenance. The scheduler advances due dates automatically — even while the account is suspended — and creates work orders on time.</p>
              <Steps items={[
                "Open <b>PPM</b> and press <b>New plan</b> to create a schedule for an asset.",
                "Set the <b>interval</b> (daily, weekly, monthly, yearly) and assign an engineer or group.",
                "Use <b>Add checklist</b> to add required tick, value, or text fields engineers must complete.",
                "Add <b>safety instructions</b>, <b>tools needed</b>, and <b>planned parts</b> to the plan.",
                "Overdue and due-soon occurrences appear in the PPM queue ready to claim.",
              ]} />
              <Tip>After a billing reactivation, deferred PPM jobs appear in a catch-up queue for a manager to release, reschedule, or dismiss — they don't flood the live queue automatically.</Tip>
            </Section>
          )}

          {can("maintenance.autonomous.manage") && (
            <Section title="Autonomous Maintenance — setup">
              <p>Autonomous Maintenance lets department team leaders complete operator-level checks without needing engineer permissions. Schedules and checklists are separate from engineering PPM.</p>
              <Steps items={[
                "Go to <b>Admin → Groups</b> and create a Maintenance-product group for the team leaders.",
                "Add the relevant users. They need a Maintenance seat but <em>not</em> engineer permissions.",
                "To let a manager run Autonomous schedules without full PPM access, assign only the <b>Manage Autonomous Maintenance</b> permission to their group.",
                "Open <b>Autonomous setup</b>, create a plan, select the group, and add checklist fields (tick, value with unit, or text).",
                "The scheduler creates due occurrences automatically. Team members complete them under <b>Autonomous</b>.",
              ]} />
            </Section>
          )}

          {product("MAINTENANCE") && !can("maintenance.ppm.manage") && (
            <Section title="Autonomous Maintenance — completing checks">
              <p>Open <b>Autonomous</b> to see checks due for groups you belong to.</p>
              <Steps items={[
                "Select a check from the list to open it.",
                "Record every required field — tick, value, or text — as prompted.",
                "Press <b>Complete checklist</b> when all required fields are filled.",
              ]} />
              <Tip>You cannot complete a check until all required fields are recorded. Optional fields can be left blank.</Tip>
            </Section>
          )}

          {can("maintenance.helpdesk.manage") && (
            <Section title="Helpdesk">
              <p>The Helpdesk handles production fault reports. Priority levels are <b>Production Can Run</b> and <b>Production Stopped</b>.</p>
              <Steps items={[
                "A ticket is raised and sits in the queue. Engineers can only claim after an escalation period.",
                "Once claimed, the engineer works the fault and records progress in notes.",
                "If production restarts before the claim is made, the ticket moves to <b>Production Resolved – Unclaimed</b>.",
                "Resolve the ticket once the fault is fixed, or convert it to a full work order for tracked repair.",
              ]} />
            </Section>
          )}

          {can("maintenance.inventory.manage") && (
            <Section title="Inventory">
              <p>Inventory tracks canonical parts with manufacturer codes, bin locations, QR labels, machine assignments and stock movements.</p>
              <Steps items={[
                "Search parts by name, code, or bin. QR scan links directly to the part record.",
                "Use <b>Receive</b> to add stock, <b>Issue</b> to consume it, and <b>Return</b> to put unused items back.",
                "Parts reserved for active work orders appear on the <b>job shelf</b> until the job closes.",
                "Low-stock alerts fire automatically when on-hand quantity falls to or below the reorder level.",
                "Export <b>Brother P-touch labels</b> from the inventory screen — the CSV is ready for import into Brother's editor.",
              ]} />
              <Tip>Use the QR scan button in the top bar to jump straight to a part or asset from its label.</Tip>
            </Section>
          )}

          {can("maintenance.assets.manage") && (
            <Section title="Assets">
              <p>Assets form a hierarchy — site, area, machine, sub-assembly. Work orders and PPM attach to individual assets.</p>
              <Steps items={[
                "Open <b>Assets</b> and use <b>New asset</b> to build the hierarchy.",
                "To import an existing register, use the Agility-compatible CSV import (Asset ID, Description, Type, Parent ID, Site, Area).",
                "The asset record shows full work-order history, PPM history, and assigned parts.",
              ]} />
            </Section>
          )}
        </>
      )}

      {/* ── Dispatch ── */}
      {product("DISPATCH") && (
        <Section title="Dispatch">
          <p>Dispatch manages outbound orders from picking through to trailer departure.</p>
          <Steps items={[
            "Create a <b>dispatch order</b> with customer, destination and expected lines.",
            "Pick and stage pallets against the order — each pallet gets a scannable QR label.",
            "Load pallets onto a <b>trailer or container</b> using two-scan verification.",
            "Create a <b>manifest</b>, get approval, and capture a driver signature before departure.",
            "Search <b>order and pallet status</b> at any point using the status view.",
          ]} />
          <Tip>The runner queue shows pallets waiting to move. Scan the pallet QR then the destination position to complete the move.</Tip>
        </Section>
      )}

      {/* ── Appointments ── */}
      {product("APPOINTMENTS") && (
        <Section title="Appointments">
          <p>Appointments handles bookings, staff calendars, and customer-facing online booking.</p>
          <Steps items={[
            "Configure <b>staff</b>, <b>services</b>, regular hours, and buffer time in Admin.",
            "Customers can book online via the public booking page, or staff can make walk-in and manual bookings.",
            "Conflict-safe scheduling prevents double-booking. A cut-off period prevents last-minute customer changes.",
            "Payment status is tracked per appointment. Pay-on-day bookings are confirmed without advance payment.",
            "24-hour reminder emails are queued automatically if SMTP is configured.",
          ]} />
        </Section>
      )}

      {/* ── Admin ── */}
      {can("platform.users.manage") && (
        <Section title="Admin — users and permissions">
          <Steps items={[
            "Go to <b>Admin</b> to create users, assign product seats, and manage group membership.",
            "Each product has a <b>20-user seat limit</b>. Assign only the seats each user needs.",
            "Groups hold a set of permissions. Assign users to the group that matches their role.",
            "Use <b>Reset password</b> to send a temporary credential if a user is locked out.",
            "Deactivating a user prevents login but preserves their audit history.",
          ]} />
          <Tip>Tenant owners always have full access and cannot be restricted by group permissions.</Tip>
        </Section>
      )}

      {owner && (
        <Section title="Admin — billing and subscriptions">
          <p>Open <b>Billing</b> to view your subscription status, access the Stripe customer portal, and see the current billing state.</p>
          <p style={{ marginTop: 8 }}>Billing states: <b>Active</b> → <b>Grace</b> (payment overdue, still operational) → <b>Restricted</b> (read-only) → <b>Terminated</b> (export only) → <b>Deleted</b>. Paying at any stage before deletion reactivates the account.</p>
        </Section>
      )}

      {/* ── Notifications ── */}
      <Section title="Notifications">
        <p>The bell icon shows your in-app notification inbox. Browser notifications are delivered while TaskRoute is open in a tab.</p>
        <p style={{ marginTop: 8 }}>Shift-targeted breakdown alerts go only to on-shift maintenance staff. Overdue PPM and low-stock alerts are generated on a schedule.</p>
        {owner && <Tip>Configure maintenance shifts in <b>Admin → Shifts</b> to target alerts at the right people.</Tip>}
      </Section>

      {/* ── Support ── */}
      <Section title="Getting support">
        <p>For issues with your workspace, contact your company administrator first.</p>
        {can("platform.support_access.manage") && (
          <p style={{ marginTop: 8 }}>Administrators can issue a <b>temporary support access code</b> from <b>Admin → Support access</b>. Codes are scoped to a specific ticket, expire automatically, and are fully audited. TaskRoute staff can only view data covered by the scopes you grant.</p>
        )}
        <p style={{ marginTop: 8 }}>Use the <b>Feedback</b> section to report a problem or suggestion directly from the interface.</p>
        <Tip>Do not enter real personal or production data in a sandbox or test workspace.</Tip>
      </Section>
    </div>
  );
}
